import { Form, Formik, FormikProps } from 'formik';
import { ScrollToFieldError } from '@synergy/components';
import { SubPageHandle, useFormikSubPage } from '@synergy/utils';
import { Theme } from '@mui/material';
import { forwardRef, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { useParams } from 'react-router';

import * as ProductDetailPricesActions from './store/product-detail-prices-actions';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { ProductDetailPricesBasicInfos } from './components/product-detail-prices-basic-infos';
import { ProductDetailPricesCurrency } from './components/product-detail-prices-currency';
import { ProductPriceFormValues } from './models/form-values/product-price-form-values';
import {
    productDetailPricesBaselineSelector,
    productDetailPricesFormDataSelector,
} from './store/product-detail-prices-selectors';
import { productDetailPricesValidationSchema } from './product-detail-prices-validation-schema';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        margin: theme.spacing(6, theme.general.pageContentSpacing),
    },
}));

export const ProductDetailPrices = forwardRef<SubPageHandle>((_, ref) => {
    const classes = useStyles();
    const { id: productIdParam } = useParams<{ id: string }>();
    const dispatch = useThunkDispatch();

    const productPrice = useAppSelector(state => state.product.detail.prices.productDetailPrice);
    const { formValues, errors, touched } = useAppSelector(productDetailPricesFormDataSelector);
    // The prices as the server has them — see PaloDirtyComparison.
    const baseline = useAppSelector(productDetailPricesBaselineSelector);
    const formikRef = useRef<FormikProps<ProductPriceFormValues>>(null);

    useEffect(() => {
        if (!productPrice && productIdParam && +productIdParam) {
            dispatch(ProductDetailPricesActions.fetchProductPrices(+productIdParam));
            dispatch(ProductDetailPricesActions.fetchSupplierOffers(+productIdParam));
        }
    }, [dispatch, productIdParam, productPrice]);

    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(ProductDetailPricesActions.updateProductPrices(values, isDirty, isValid, errs));
    });

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            validationSchema={productDetailPricesValidationSchema}
            initialValues={formValues}
            initialErrors={errors}
            innerRef={formikRef}
            initialTouched={touched}
            enableReinitialize={true}
        >
            {formikProps => (
                <Form className={classes.container}>
                    <PaloDirtyComparison baseline={baseline} />
                    <ScrollToFieldError />
                    <ProductDetailPricesBasicInfos />
                    {formikProps.values.currencyPrices?.map((currencyPrice, index) => (
                        <ProductDetailPricesCurrency key={index} index={index} currencyPrice={currencyPrice} />
                    ))}
                </Form>
            )}
        </Formik>
    );
});
